import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { money, todayISO, addDays, calcTotals, lineTotal, round2, PAYMENT_METHODS } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader, Empty } from "@/components/kit";
import { ClientPicker, ProductPicker } from "@/components/Pickers";
import { ClientDialog } from "@/components/ClientDialog";

const snap = (c) => ({ name: c.name, id_number: c.id_number, email: c.email, phone: c.phone, address: c.address });
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="label-cap">{label}</Label>{children}</div>;

// Editor compartido: proformas (crear/editar) y facturas (crear)
export default function DocEditor({ kind }) {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { settings, base } = useApp();
  const isQ = kind === "quote";
  const path = isQ ? "quotes" : "invoices";
  const seg = isQ ? "proformas" : "facturas";
  const [doc, setDoc] = useState(() => ({
    client_id: null, client: {}, items: [], global_discount: 0, iva: settings.iva, currency: settings.currency,
    exchange_rate: settings.exchange_rate, notes: "", issue_date: todayISO(),
    valid_until: isQ ? addDays(todayISO(), 15) : null, due_date: isQ ? null : addDays(todayISO(), 30), payment_method: "Efectivo",
  }));
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const pickClient = (c) => set({ client_id: c.id, client: snap(c) });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/clients").then((r) => {
      setClients(r.data);
      const c = r.data.find((x) => x.id === sp.get("cliente"));
      if (c && !id) setDoc((d) => ({ ...d, client_id: c.id, client: snap(c) }));
    });
    if (id) api.get(`/${path}/${id}`).then((r) => setDoc(r.data));
  }, [id, path, sp]);

  const rate = doc.currency === "USD" ? Number(doc.exchange_rate) || 1 : 1;
  const addProduct = (p) => {
    const i = doc.items.findIndex((x) => x.product_id === p.id);
    if (i >= 0) return updItem(i, { qty: Number(doc.items[i].qty) + 1 });
    set({ items: [...doc.items, { product_id: p.id, code: p.code, name: p.name, description: p.description, type: p.type, qty: 1, price: round2(p.sale_price / rate), discount: 0, taxable: true }] });
  };
  const addFree = () => set({ items: [...doc.items, { product_id: null, code: "", name: "", description: "", type: "servicio", qty: 1, price: 0, discount: 0, taxable: true }] });
  const updItem = (i, patch) => set({ items: doc.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const delItem = (i) => set({ items: doc.items.filter((_, j) => j !== i) });
  const changeCurrency = (cur) => {
    if (cur === doc.currency) return;
    const r = Number(doc.exchange_rate) || 1;
    const k = cur === "USD" ? 1 / r : r;
    set({ currency: cur, items: doc.items.map((it) => ({ ...it, price: round2(Number(it.price) * k) })) });
  };

  const t = calcTotals(doc.items, doc.global_discount, doc.iva);

  const save = async () => {
    if (!doc.items.some((it) => it.name.trim())) return toast.error("Agregue al menos un artículo");
    setSaving(true);
    const num = (v) => Number(v) || 0;
    const body = {
      ...doc, global_discount: num(doc.global_discount), iva: num(doc.iva), exchange_rate: doc.currency === "USD" ? num(doc.exchange_rate) : 1,
      items: doc.items.map((it) => ({ ...it, qty: num(it.qty), price: num(it.price), discount: num(it.discount) })),
    };
    try {
      const { data } = id ? await api.put(`/${path}/${id}`, body) : await api.post(`/${path}`, body);
      toast.success(`${isQ ? "Proforma" : "Factura"} ${data.number} guardada`);
      nav(`${base}/${seg}/${data.id}`, { state: { autoSend: !id && settings.auto_email !== false } });
    } catch (e) {
      toast.error(errMsg(e));
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title={id ? `Editar proforma ${doc.number || ""}` : isQ ? "Nueva proforma" : "Nueva factura"} subtitle="Seleccione el cliente, agregue artículos y revise los totales">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="editor-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Volver</Button>
        <Button className="btn-neon" onClick={save} disabled={saving} data-testid="editor-save-btn"><Save className="w-4 h-4 mr-1" />Guardar</Button>
      </PageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="panel p-5 xl:col-span-2 space-y-4">
          <F label="Cliente">
            <ClientPicker clients={clients} value={doc.client} onPick={pickClient} onNew={() => setNewClient(true)} />
          </F>
          {doc.client?.name && (
            <div className="text-xs text-slate-400 grid sm:grid-cols-2 gap-1" data-testid="editor-client-info">
              <span>Cédula: <span className="mono text-slate-300">{doc.client.id_number || "—"}</span></span>
              <span>Tel: {doc.client.phone || "—"}</span><span>{doc.client.email}</span><span>{doc.client.address}</span>
            </div>
          )}
        </div>
        <div className="panel p-5 grid grid-cols-2 gap-3">
          <F label="Fecha emisión"><Input type="date" value={doc.issue_date} onChange={(e) => set({ issue_date: e.target.value })} data-testid="editor-issue-date" /></F>
          {isQ ? (
            <F label="Vigencia hasta"><Input type="date" value={doc.valid_until || ""} onChange={(e) => set({ valid_until: e.target.value })} data-testid="editor-valid-until" /></F>
          ) : (
            <F label="Vencimiento"><Input type="date" value={doc.due_date || ""} onChange={(e) => set({ due_date: e.target.value })} data-testid="editor-due-date" /></F>
          )}
          <F label="Moneda">
            <Select value={doc.currency} onValueChange={changeCurrency}>
              <SelectTrigger data-testid="editor-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="CRC">Colones ₡</SelectItem><SelectItem value="USD">Dólares $</SelectItem></SelectContent>
            </Select>
          </F>
          {doc.currency === "USD" ? (
            <F label="Tipo de cambio"><Input type="number" value={doc.exchange_rate} onChange={(e) => set({ exchange_rate: e.target.value })} data-testid="editor-exchange-rate" /></F>
          ) : (
            <F label="Forma de pago">
              <Select value={doc.payment_method} onValueChange={(v) => set({ payment_method: v })}>
                <SelectTrigger data-testid="editor-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          )}
          {doc.currency === "USD" && (
            <div className="col-span-2">
              <F label="Forma de pago">
                <Select value={doc.payment_method} onValueChange={(v) => set({ payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
          )}
        </div>
      </div>

      <div className="panel p-5 mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <span className="label-cap">Detalle de artículos</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addFree} data-testid="add-free-line-btn"><Plus className="w-4 h-4 mr-1" />Línea libre</Button>
            <ProductPicker products={products} onPick={addProduct} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="editor-items-table">
            <thead>
              <tr className="text-left label-cap">
                <th className="pb-2">Artículo</th><th className="w-24">Cant.</th><th className="w-36">Precio</th><th className="w-24">Desc. %</th>
                <th className="w-16 text-center">IVA</th><th className="w-36 text-right">Total</th><th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it, i) => (
                <tr key={i} className="border-t border-[#271E56] align-top" data-testid={`editor-item-${i}`}>
                  <td className="py-2 pr-2">
                    <Input value={it.name} onChange={(e) => updItem(i, { name: e.target.value })} placeholder="Descripción del artículo" data-testid={`item-name-${i}`} />
                    {it.code && <div className="mono text-[11px] text-slate-500 mt-1">{it.code}</div>}
                  </td>
                  <td className="py-2 pr-2"><Input type="number" min="0" value={it.qty} onChange={(e) => updItem(i, { qty: e.target.value })} data-testid={`item-qty-${i}`} /></td>
                  <td className="py-2 pr-2"><Input type="number" min="0" value={it.price} onChange={(e) => updItem(i, { price: e.target.value })} data-testid={`item-price-${i}`} /></td>
                  <td className="py-2 pr-2"><Input type="number" min="0" max="100" value={it.discount} onChange={(e) => updItem(i, { discount: e.target.value })} data-testid={`item-discount-${i}`} /></td>
                  <td className="py-2 text-center pt-4"><Switch checked={it.taxable !== false} onCheckedChange={(v) => updItem(i, { taxable: v })} data-testid={`item-taxable-${i}`} /></td>
                  <td className="py-2 text-right mono text-white pt-4" data-testid={`item-total-${i}`}>{money(lineTotal(it), doc.currency)}</td>
                  <td className="py-2 text-right"><Button size="icon" variant="ghost" className="text-rose-400" onClick={() => delItem(i)} data-testid={`item-remove-${i}`}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!doc.items.length && <Empty text="Agregue artículos desde el inventario o una línea libre" />}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <div className="panel p-5 xl:col-span-2">
          <F label="Notas (aparecen en el documento)"><Textarea rows={4} value={doc.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="editor-notes" /></F>
        </div>
        <div className="panel p-5 space-y-3" data-testid="editor-totals">
          <div className="grid grid-cols-2 gap-3">
            <F label="Descuento global %"><Input type="number" min="0" max="100" value={doc.global_discount} onChange={(e) => set({ global_discount: e.target.value })} data-testid="editor-global-discount" /></F>
            <F label="IVA %"><Input type="number" min="0" value={doc.iva} onChange={(e) => set({ iva: e.target.value })} data-testid="editor-iva" /></F>
          </div>
          <Row label="Subtotal" v={money(t.subtotal, doc.currency)} id="subtotal" />
          <Row label="Descuento" v={`− ${money(t.discount, doc.currency)}`} id="discount" />
          <Row label={`IVA (${doc.iva || 0}%)`} v={money(t.tax, doc.currency)} id="tax" />
          <div className="flex justify-between items-center pt-3 border-t border-[#271E56]">
            <span className="font-display font-bold text-white">TOTAL</span>
            <span className="mono text-2xl font-bold text-[#00F0FF]" data-testid="editor-total">{money(t.total, doc.currency)}</span>
          </div>
        </div>
      </div>
      <ClientDialog open={newClient} onOpenChange={setNewClient} client={null} onSaved={(c) => { setClients((l) => [...l, c]); pickClient(c); }} />
    </div>
  );
}

const Row = ({ label, v, id }) => (
  <div className="flex justify-between text-sm"><span className="text-slate-400">{label}</span><span className="mono text-slate-200" data-testid={`editor-${id}`}>{v}</span></div>
);
